import type {
  MissionDetail,
  MissionTaskGraphNode,
  MissionTaskSummary,
} from "@/services/api/agentic-v2";
import { missionUsesPerTaskStageGrouping } from "./missionTaskStageBuckets";

const TERMINAL_TASK_STATUSES = new Set([
  "done",
  "completed",
  "auto_closed",
  "cancelled",
  "canceled",
]);

function isTerminalTaskStatus(status: string): boolean {
  return TERMINAL_TASK_STATUSES.has(status.toLowerCase());
}

/** When WS appends a second row for the same graph slot, keep the live one. */
export function dedupeMissionTasksForGraph(
  tasks: MissionTaskSummary[],
  graph: MissionTaskGraphNode[],
): MissionTaskSummary[] {
  const byNodeKey = new Map<string, MissionTaskSummary>();
  const unslotted: MissionTaskSummary[] = [];

  for (const task of tasks) {
    const node = findGraphNodeForTask(task, graph);
    if (!node) {
      unslotted.push(task);
      continue;
    }
    const existing = byNodeKey.get(node.key);
    if (!existing) {
      byNodeKey.set(node.key, task);
      continue;
    }
    byNodeKey.set(node.key, preferMissionTaskForGraphSlot(existing, task));
  }

  return [...byNodeKey.values(), ...unslotted];
}

function preferMissionTaskForGraphSlot(
  left: MissionTaskSummary,
  right: MissionTaskSummary,
): MissionTaskSummary {
  if (left._id === right._id) return right;
  const leftTerminal = isTerminalTaskStatus(left.status);
  const rightTerminal = isTerminalTaskStatus(right.status);
  if (leftTerminal && !rightTerminal) return right;
  if (!leftTerminal && rightTerminal) return left;
  return right.updated_at >= left.updated_at ? right : left;
}

export type TimelineRow =
  | { kind: "real"; node: MissionTaskGraphNode | null; task: MissionTaskSummary }
  | { kind: "upcoming"; node: MissionTaskGraphNode };

/**
 * Build per-stage timeline rows by walking the template task graph and
 * overlaying real tasks. When any task carries a ``stage`` field, that value
 * is authoritative for placement — not the deduped graph node's stage.
 */
export function buildRowsByStage(
  mission: MissionDetail,
): Record<string, TimelineRow[]> {
  const result: Record<string, TimelineRow[]> = {};
  for (const stage of mission.stages) {
    result[stage.name] = [];
  }

  const graph = mission.task_graph ?? [];
  const tasks = dedupeMissionTasksForGraph(mission.tasks, graph);
  const usePerTaskStages = missionUsesPerTaskStageGrouping(tasks);
  const knownStages = new Set(mission.stages.map((s) => s.name));

  for (const node of graph) {
    const stageName = node.stage ?? mission.stages[0]?.name ?? "";
    if (!result[stageName]) result[stageName] = [];

    if (!hasRealTaskForGraphNode(node, tasks)) {
      result[stageName].push({ kind: "upcoming", node });
    }
  }

  for (const task of tasks) {
    const node = findGraphNodeForTask(task, graph);
    const stageName = resolveTimelineStageForTask(
      mission,
      task,
      node,
      usePerTaskStages,
      knownStages,
    );
    if (!result[stageName]) result[stageName] = [];
    result[stageName].push({ kind: "real", node, task });
  }

  return result;
}

/** True when a spawned or catalogue-matched task already occupies a graph slot. */
export function hasRealTaskForGraphNode(
  node: MissionTaskGraphNode,
  tasks: MissionTaskSummary[],
): boolean {
  return tasks.some((task) => taskMatchesGraphNode(task, node));
}

export function taskMatchesGraphNode(
  task: MissionTaskSummary,
  node: MissionTaskGraphNode,
): boolean {
  const spawnedKey = task.spawned_from_node?.trim();
  if (spawnedKey && spawnedKey === node.key) return true;
  return task.catalogue_id === node.catalogue_id;
}

/** Prefer ``spawned_from_node`` so branched instances map to the right graph row. */
export function findGraphNodeForTask(
  task: MissionTaskSummary,
  graph: MissionTaskGraphNode[],
): MissionTaskGraphNode | null {
  const spawnedKey = task.spawned_from_node?.trim();
  if (spawnedKey) {
    const bySpawn = graph.find((node) => node.key === spawnedKey);
    if (bySpawn) return bySpawn;
  }
  return graph.find((node) => node.catalogue_id === task.catalogue_id) ?? null;
}

export function resolveTimelineStageForTask(
  mission: MissionDetail,
  task: MissionTaskSummary,
  node: MissionTaskGraphNode | null,
  usePerTaskStages: boolean,
  knownStages: Set<string>,
): string {
  const firstStage = mission.stages[0]?.name ?? "";

  if (usePerTaskStages) {
    const taskStage = task.stage?.trim();
    if (taskStage && knownStages.has(taskStage)) return taskStage;

    const current = mission.current_stage?.trim();
    if (current && knownStages.has(current)) return current;

    return node?.stage?.trim() && knownStages.has(node.stage.trim())
      ? node.stage.trim()
      : firstStage;
  }

  return mission.current_stage ?? firstStage;
}
